import { anyAbortSignal } from "@videojs/utils/events";
import { generateId } from "@videojs/utils/string";
//#region src/core/tasks/task.ts
/**
* Generic reusable task that wraps an async run function.
*
* Owns its own AbortController so it can always be aborted independently. Optionally composes an external AbortSignal
* so that a parent's cancellation propagates into the task's work without requiring the caller to track the task
* separately.
*
* `run()` is memoized: the work runs at most once per instance, and every call returns the same promise (so observers
* can `await run()` to read the result without re-triggering the work). To re-run the _same_ work, take a `clone()` — a
* fresh instance with its own AbortController and a pending state.
*
* Ordering guarantee: `value` is written before `status` transitions to `'done'`; `error` is written before `status`
* transitions to `'error'`. Any reader observing `status === 'done'` is guaranteed `value` is already present.
*/
var Task = class Task {
	id;
	#runFn;
	#externalSignal;
	#abortController = new AbortController();
	#signal;
	#status = "pending";
	#value = void 0;
	#error = void 0;
	#previous = void 0;
	#promise = void 0;
	constructor(runFn, config) {
		this.#runFn = runFn;
		const rawId = config?.id;
		this.id = typeof rawId === "function" ? rawId() : rawId ?? generateId();
		this.#externalSignal = config?.signal;
		this.#signal = config?.signal ? anyAbortSignal([this.#abortController.signal, config.signal]) : this.#abortController.signal;
	}
	get status() {
		return this.#status;
	}
	get value() {
		return this.#value;
	}
	get error() {
		return this.#error;
	}
	get previous() {
		return this.#previous;
	}
	get signal() {
		return this.#signal;
	}
	run() {
		this.#promise ??= this.#execute();
		return this.#promise;
	}
	async #execute() {
		this.#status = "running";
		try {
			const result = await this.#runFn(this.#signal);
			this.#value = result;
			this.#status = "done";
			return result;
		} catch (e) {
			this.#error = e;
			this.#status = "error";
			throw e;
		}
	}
	abort() {
		this.#abortController.abort();
	}
	/**
	* A fresh task with the same work, id, and external signal, in a pending state (its own AbortController, no memoized
	* result) — so it can be run again. Used to re-run structurally identical work (e.g. `RecurringRunner` reloads).
	*
	* The clone inherits this run's value as its `previous` (or this run's own `previous` if it never produced one — e.g.
	* it errored), so a recurrence's `previous` always tracks the last _successful_ value across the lineage with no
	* bookkeeping in the runner.
	*/
	clone() {
		const cloned = new Task(this.#runFn, {
			id: this.id,
			signal: this.#externalSignal
		});
		cloned.#previous = this.#status === "done" ? this.#value : this.#previous;
		return cloned;
	}
};
/**
* Runs tasks concurrently, deduplicated by task id.
*
* If a task with a given id is already in flight, subsequent schedule() calls for that id are silently ignored until
* the first completes. Tasks are stored so abortAll() can cancel any in-flight work (e.g. on engine cleanup).
*/
var ConcurrentRunner = class {
	#pending = /* @__PURE__ */ new Map();
	#settled = Promise.resolve();
	#resolveSettled = null;
	#destroyed = false;
	schedule(task) {
		if (this.#destroyed) return Promise.resolve();
		const existing = this.#pending.get(task.id);
		if (existing) return existing.promise;
		if (this.#pending.size === 0) this.#settled = new Promise((resolve) => {
			this.#resolveSettled = resolve;
		});
		const promise = task.run();
		promise.catch(() => {});
		const cleanup = () => {
			this.#pending.delete(task.id);
			if (this.#pending.size === 0) {
				this.#resolveSettled?.();
				this.#resolveSettled = null;
			}
		};
		promise.then(cleanup, cleanup);
		this.#pending.set(task.id, {
			task,
			promise
		});
		return promise;
	}
	/**
	* Registers a callback to fire when all currently in-flight tasks settle. If the runner is already idle, the callback
	* is never called. If abortAll() is called before the batch settles, the callback is superseded and silently dropped
	* — no stale callbacks, no generation token required by the caller.
	*/
	whenSettled(callback) {
		if (this.#pending.size === 0) return;
		const captured = this.#settled;
		captured.then(() => {
			if (this.#settled !== captured) return;
			callback();
		}, () => {});
	}
	abortAll() {
		for (const { task } of this.#pending.values()) task.abort();
		this.#pending.clear();
		this.#resolveSettled?.();
		this.#resolveSettled = null;
		this.#settled = Promise.resolve();
	}
	destroy() {
		this.#destroyed = true;
		this.abortAll();
	}
};
/**
* Runs tasks one at a time in submission order.
*
* Each schedule() call returns a Promise that resolves or rejects with the task's result when it is eventually
* executed. Tasks wait in queue until the prior task completes.
*
* Serialization is achieved by chaining each task's run() onto the tail of a shared promise chain — no explicit queue
* or drain loop needed.
*
* AbortAll() aborts all pending (not yet started) tasks and the currently in-flight task. Pending tasks still run
* briefly but receive an aborted signal and are expected to exit early.
*/
var SerialRunner = class {
	#chain = Promise.resolve();
	#pending = /* @__PURE__ */ new Set();
	#current = null;
	#destroyed = false;
	schedule(task) {
		if (this.#destroyed) return Promise.resolve();
		const t = task;
		this.#pending.add(t);
		const result = this.#chain.then(() => {
			this.#pending.delete(t);
			this.#current = t;
			return task.run();
		}).finally(() => {
			this.#current = null;
		});
		this.#chain = result.then(() => {}, () => {});
		return result;
	}
	/**
	* A promise that resolves when all currently-scheduled tasks have settled. Use the reference as a generation token:
	* capture it after scheduling a batch, then check identity in the resolution callback to detect whether a subsequent
	* abortAll() + new batch has superseded this one.
	*/
	get settled() {
		return this.#chain;
	}
	/**
	* Registers a callback to fire when all currently-pending tasks settle. If the runner is already idle (no pending or
	* running tasks), the callback is never called. If new tasks are scheduled before the current batch settles, the
	* callback is superseded and silently dropped — no stale callbacks, no generation token required by the caller.
	*/
	whenSettled(callback) {
		if (this.#pending.size === 0 && this.#current === null) return;
		const currentChain = this.#chain;
		currentChain.then(() => {
			if (this.#chain !== currentChain) return;
			callback();
		}, () => {});
	}
	/** Aborts and clears queued tasks without touching the in-flight task. */
	abortPending() {
		for (const task of this.#pending) task.abort();
		this.#pending.clear();
	}
	abortAll() {
		this.abortPending();
		this.#current?.abort();
	}
	destroy() {
		this.#destroyed = true;
		this.abortAll();
	}
};
/**
* A {@link Reschedule} that never recurs — the task runs exactly once. Pass it to a {@link RecurringRunner} for
* non-recurring, run-once work (e.g. resolving a complete VoD playlist that can never go stale).
*/
const runOnce = () => Promise.resolve(false);
/**
* Runs a task, then re-runs it whenever a {@link Reschedule} function says to, until it says stop (or it's aborted) —
* the recurring sibling of {@link ConcurrentRunner} / {@link SerialRunner}, and like them it's handed a
* {@link TaskLike} to run.
*
* The runner has no notion of time: it just awaits whatever `reschedule` returns (resolves `true` → re-run; `false` →
* stop). A `reschedule` is required; pass {@link runOnce} for non-recurring, run-exactly-once work.
*
* Single-slot, keyed by task **id**: there is always at most one identified active task for re-running. Scheduling a
* task whose id matches the active one is a no-op — the existing recurrence keeps running (dedup by id). Scheduling a
* task with a _different_ id aborts the prior task's in-flight run and pending reschedule, then takes over the slot
* (abort-and-replace) — the right shape when there's one logical unit of recurring work (e.g. reloading the _selected_
* track's media playlist).
*
* Each re-run is a fresh `clone()` of the task (since `Task.run()` is memoized — the same instance won't re-execute),
* carrying the same id so the slot's identity is stable across cycles. The clone also carries the prior cycle's value
* forward as `task.previous`. The run function should read any inputs that change between cycles at call time rather
* than capturing them once.
*
* The task is the sole cancellation channel: `abortAll()` aborts the active task, which fires `task.signal` —
* cancelling both its in-flight run and any pending reschedule delay waiting on that signal. An aborted (or stopped)
* recurrence frees the slot, so a later schedule of the same id starts fresh.
*/
var RecurringRunner = class {
	#reschedule;
	#active = null;
	#destroyed = false;
	constructor(reschedule) {
		this.#reschedule = reschedule;
	}
	/**
	* Run `task` and recur per the `reschedule` verdict, as a single promise. Resolves with the _final_ cycle's value
	* when the recurrence stops; **rejects** if a run (or reschedule) genuinely fails — the rejection propagates to the
	* caller, who owns error handling; the runner only frees its slot (no swallowing). The runner's _own_ cancellation
	* (abort/supersede/destroy) is not a failure, so an aborted recurrence settles quietly rather than rejecting —
	* callers don't have to `.catch` routine teardown.
	*
	* Each cycle runs the task and consults `reschedule` concurrently (so the delay can be measured from the run's
	* start); when both settle and this cycle still owns the slot, a `true` verdict re-schedules a `clone()` whose
	* promise is _returned_ — so the recurrence is the method calling itself, threaded into one promise, no separate
	* loop. The clone shares the id, so the slot's identity is stable across cycles; it's released just before the
	* re-schedule so the call advances rather than dedup-returning.
	*
	* Note: because each cycle's promise adopts the next, the chain retains every prior cycle for the life of the
	* recurrence — bounded for finite recurrences, an unbounded (small per-cycle) cost for a long-lived one (e.g. live
	* reload).
	*/
	schedule(task) {
		if (this.#destroyed) return Promise.resolve();
		if (this.#active?.id === task.id) return this.#active.run();
		this.#cancel();
		this.#active = task;
		return Promise.all([task.run(), this.#reschedule(task)]).then(([value, again]) => {
			if (this.#active === task && again && !task.signal.aborted) {
				this.#active = null;
				return this.schedule(task.clone());
			}
			if (this.#active === task) this.#active = null;
			return value;
		}, (error) => {
			if (this.#active === task) this.#active = null;
			if (task.signal.aborted) return void 0;
			throw error;
		});
	}
	#cancel() {
		this.#active?.abort();
		this.#active = null;
	}
	abortAll() {
		this.#cancel();
	}
	destroy() {
		this.#destroyed = true;
		this.abortAll();
	}
};
//#endregion
export { ConcurrentRunner, RecurringRunner, SerialRunner, Task, runOnce };

//# sourceMappingURL=task.js.map